import { Middleware, Dispatch, Action, MiddlewareAPI } from "../lib/redux";
import { GlobalReducer, IGlobalReducer, StoreActions } from "./reducers";
import storage from "../utils/storage/storage";
import { setClockDrift } from "../utils/storage/date";
import { setItem, isNativeStorageAvailable } from "../utils/native/secureStorageApi";
import { DriveReducerNames, REDUCER_KEYS_STILL_IN_LOCALSTORAGE, reducersToWriteToStorage } from "./reducerPersistence";
import { IndexedDbStorage } from "../utils/storage/indexedDbStorage";
import IndexedDbMigrationFromRedux from "../utils/storage/indexedDbMigrationFromRedux";
import { DRIVE_STORAGE_PREFIX, DRIVE_REDUX_PREFIX, getFullReducerStorageKey } from "./storageConstants";

type DriveMiddleware = Middleware<any, GlobalReducer, Dispatch<Action<StoreActions>>>;

// Re-export for backwards compatibility
export { DRIVE_STORAGE_PREFIX, DRIVE_REDUX_PREFIX, getFullReducerStorageKey };

export const middlewareLogger: DriveMiddleware = () => (next: Dispatch<Action<StoreActions>>) => (action: Action<StoreActions>) => {
    // TODO: Investigate action logging for time-travel / debug
    return next(action);
};

/**
 * Sets `../utils/storage/date` clockDriftMs cache state on every store update
 */
export const setClockDriftMiddleware: DriveMiddleware = _api => (next: Dispatch<Action<StoreActions>>) => (action: Action<StoreActions>) => {
    let output = next(action);
    setClockDrift(_api.getState().data.clockDriftMs);
    return output;
};

const actionTypeToReducerMapping = Object.keys(reducersToWriteToStorage).reduce((accumulator, key) => {
    const actionTypes = reducersToWriteToStorage[key].types as any;
    Object.keys(actionTypes).forEach(type => {
        accumulator[actionTypes[type]] = key;
    });
    return accumulator;
}, {});

const isStillRequiredInLocalStorage = (reducer: DriveReducerNames): boolean => {
    return REDUCER_KEYS_STILL_IN_LOCALSTORAGE.has(`${ DRIVE_REDUX_PREFIX + reducer }`);
};

function writeToStorage(api: MiddlewareAPI<Dispatch<Action<StoreActions>>, GlobalReducer>, reducer: DriveReducerNames, isSecureStorage = false, callbackFn) {
    let state = api.getState();
    if (!state.hasOwnProperty(reducer)) {
        return;
    }

    // Separate storage keys by reducer
    let currentState = state[reducer];
    if (typeof callbackFn !== "undefined") {
        currentState = callbackFn(state[reducer]);
    }

    // only write to secure storage when available, otherwise local storage
    if (isSecureStorage && isNativeStorageAvailable()) {
        void setItem(`@store_${ reducer }`, currentState);
    } else {
        const fullKey = getFullReducerStorageKey(reducer);
        if (IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            // If migration/rollback is in progress, queue the reducer key instead of writing directly
            // processQueue will read the latest value from Redux state after migration/rollback completes
            if (IndexedDbMigrationFromRedux.activeMigrationOrRollback) {
                IndexedDbMigrationFromRedux.queueWrite(reducer as keyof IGlobalReducer);
            } else {
                void IndexedDbStorage
                    .saveData(fullKey, currentState)
                    .catch(_ => {
                        // Silence because IndexedDbStorage transaction errors are already logged
                    });
            }

            if (isStillRequiredInLocalStorage(reducer)) {
                storage.set(fullKey, currentState, undefined, true);
            }
        } else {
            storage.set(fullKey, currentState, undefined, true);
        }
    }
}

/**
 * Creates a database storage middleware with the specified configuration.
 * @param isIndexedDBEnabled Whether to use IndexedDB (true) or localStorage (false)
 */
export const createDatabaseStorageMiddleware = (): DriveMiddleware => {
    return api => (next: Dispatch<Action<StoreActions>>) => (action: Action<StoreActions>) => {
        let output = next(action);
        let reducer = actionTypeToReducerMapping[action.type];
        if (!reducer) {
            return output;
        }

        let callbackFn = reducersToWriteToStorage[reducer].callbackFn;
        let isSecureStorage = reducersToWriteToStorage[reducer].isSecureStorage || false;
        writeToStorage(api, reducer, isSecureStorage, callbackFn);
        return output;
    };
};
